import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { bookingApi, unwrap, type ApiEnvelope } from '@/lib/api/client';
import type { BookingLocation, MembershipPlan } from '@/features/memberships/types';
import type {
  PagedSubscriptions,
  SubscriptionDetail,
  SubscriptionPayment,
  UserSearchResult,
} from './types';

const get = <T>(url: string) => unwrap<T>(bookingApi.get<ApiEnvelope<T>>(url));

/**
 * `MembershipSearchModel` — the one payload behind search, dashboard and export.
 *
 * `status` and `paymentState` use 0 to mean "all" rather than being omitted,
 * unlike most filters on this API; `locationId`, `subscriptionId` and `origin`
 * are nullable in the usual way.
 */
export interface SubscriptionSearchModel {
  /**
   * Both nullable on the API, and both left off: the page has no date filter,
   * so a window here would silently hide memberships nobody asked to exclude.
   * Send them together with a `dateBasis` if a date filter is ever added.
   */
  startDate?: string;
  endDate?: string;
  dateBasis: number;
  locationId: string | null;
  subscriptionId: string | null;
  status: number;
  origin: number | null;
  paymentState: number;
  autoRenew: boolean | null;
  /** Seats bought as part of a multi-member plan appear as their own rows. */
  includeMemberSeats: boolean;
  query: string;
  sort: number;
  page: number;
  size: number;
}

export function useSubscriptionSearch(
  model: SubscriptionSearchModel,
  enabled = true
) {
  return useQuery({
    queryKey: ['manage-subscriptions', 'search', model],
    queryFn: () =>
      unwrap<PagedSubscriptions>(
        bookingApi.post('v2/admin/memberships/search', model)
      ),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function useExportSubscriptions() {
  return useMutation({
    mutationFn: (model: SubscriptionSearchModel) =>
      unwrap<Record<string, unknown>[]>(
        bookingApi.post('v2/admin/memberships/export', model)
      ),
  });
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: ['manage-subscriptions'] });
}

/** Cancels the enrolment. Succeeds with an empty message, hence the caller's fallback. */
export function useCancelSubscription() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (id: string) =>
      unwrap<unknown>(bookingApi.delete(`v2/admin/memberships/${id}`)),
    onSuccess: invalidate,
  });
}

/**
 * `PUT v2/admin/memberships/{id}` — `UpdateMembershipModel`.
 *
 * Five fields, and no more: the plan it is for moves through its own endpoint,
 * and auto-renew and comments are not editable here despite appearing on the
 * row.
 */
export interface UpdateSubscriptionInput {
  id: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  wasSponsored: boolean;
  countForSubscriptionLimit: boolean;
}

export function useUpdateSubscription() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({ id, ...body }: UpdateSubscriptionInput) =>
      unwrap<unknown>(bookingApi.put(`v2/admin/memberships/${id}`, body)),
    onSuccess: invalidate,
  });
}

/**
 * `POST v2/admin/memberships` — `OnBehalfUserSubscribeBindingModel`.
 *
 * The payment fields are optional and only sent when money actually changed
 * hands: a zero would create a meaningless payment row, while sending nothing
 * leaves the subscription counting as unbilled, which is the honest state for
 * "not paid yet".
 */
export interface CreateSubscriptionInput {
  subscriptionId: string;
  userId: string;
  countForSubscriptionLimit: boolean;
  allowPromo: boolean;
  wasSponsored: boolean;
  startDate: string;
  endDate: string;
  amountReceived?: number;
  paymentMethod?: number;
  offlineReference?: string;
  adminNotes?: string;
}

/** Returns the new subscription's id, so the caller can open it. */
export function useCreateSubscription() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (body: CreateSubscriptionInput) =>
      unwrap<string>(bookingApi.post('v2/admin/memberships', body)),
    onSuccess: invalidate,
  });
}

/** `PUT v2/admin/memberships/{id}/plan` — move an enrolment onto another plan. */
export function useChangeSubscriptionPlan() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({ id, newSubscriptionId }: { id: string; newSubscriptionId: string }) =>
      unwrap<unknown>(
        bookingApi.put(`v2/admin/memberships/${id}/plan`, { newSubscriptionId })
      ),
    onSuccess: invalidate,
  });
}

// ------------------------------------------------------------------ detail

/**
 * Everything about one subscription in a single request.
 *
 * The page this replaces made the admin press "Load payments" and "Load
 * benefits" per row, so a row with no visible payment was indistinguishable
 * from one nobody had loaded.
 */
export function useSubscriptionDetail(id: string | null) {
  return useQuery({
    queryKey: ['manage-subscriptions', 'detail', id],
    queryFn: () => get<SubscriptionDetail>(`v2/admin/memberships/${id}`),
    enabled: !!id,
  });
}

function useInvalidateDetail(id: string | null) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({
      queryKey: ['manage-subscriptions', 'detail', id],
    });
    void queryClient.invalidateQueries({
      queryKey: ['manage-subscriptions', 'search'],
    });
  };
}

/** Undoes a hand-recorded payment. Money taken by a provider has to be refunded instead. */
export function useVoidSubscriptionPayment(subscriptionId: string | null) {
  const invalidate = useInvalidateDetail(subscriptionId);

  return useMutation({
    mutationFn: (paymentId: string) =>
      unwrap<unknown>(
        bookingApi.delete(`v2/admin/memberships/payments/${paymentId}`)
      ),
    onSuccess: invalidate,
  });
}

/** Sends a provider-taken payment back through the provider. */
export function useRefundSubscriptionPayment(subscriptionId: string | null) {
  const invalidate = useInvalidateDetail(subscriptionId);

  return useMutation({
    mutationFn: (paymentId: string) =>
      unwrap<SubscriptionPayment[]>(
        bookingApi.post(`v2/admin/memberships/payments/${paymentId}/refund`, null)
      ),
    onSuccess: invalidate,
  });
}

// ------------------------------------------------------------- user lookup

/** `POST admin/users/search` — who a new subscription is for. */
export function useUserSearch() {
  return useMutation({
    mutationFn: (searchTerm: string) =>
      unwrap<UserSearchResult[]>(
        bookingApi.post('admin/users/search', { searchTerm })
      ),
  });
}

// ---------------------------------------------------------- filter sources

export function useBookingLocations() {
  return useQuery({
    queryKey: ['booking', 'locations'],
    queryFn: () => get<BookingLocation[]>('locations'),
    staleTime: 5 * 60 * 1000,
  });
}

/** The plans a subscription can be to. */
export function useSubscriptionPlans() {
  return useQuery({
    queryKey: ['memberships', 'plans'],
    queryFn: () => get<MembershipPlan[]>('subscriptions'),
    staleTime: 5 * 60 * 1000,
  });
}
